import { createFileRoute } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2, Clock, CheckCircle2, Facebook, Instagram, Linkedin, Youtube, Twitter } from "lucide-react";
const SubmittedToast = lazy(() =>
  import("@/components/site/ContactSubmittedToast").then((m) => ({ default: m.ContactSubmittedToast })),
);

import { useTranslation } from "react-i18next";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { fetchContactInfo } from "@/lib/content";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { webPageScript } from "@/lib/webpage-jsonld";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact & Support | Online Tax VAT Bangladesh" },
      { name: "description", content: "Contact Online Tax VAT Bangladesh for support with income tax return filing, VAT registration, VAT return filing and TIN services." },
      { property: "og:title", content: "Contact VAT Online Bangladesh — Tax & VAT Advisor Support" },
      { property: "og:description", content: "Contact our support team by WhatsApp, email or hotline for tax and VAT service help." },
      { property: "og:url", content: "https://onlinetaxvat.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/contact" }],
    scripts: [webPageScript({
      path: "/contact",
      name: "Contact — Online Tax VAT Bangladesh",
      description: "Contact Online Tax VAT Bangladesh for support with income tax return filing, VAT registration, VAT return filing and TIN services.",
    })],
  }),
  component: ContactPage,
});

function ContactPage() {
  const { t, i18n } = useTranslation();
  const [info, setInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => { fetchContactInfo().then((d) => { setInfo(d); setLoading(false); }); }, []);

  const items = info ? [
    { icon: Phone, label: t("contactPage.hotline"), value: info.phone || "—", href: info.phone ? `tel:${info.phone}` : undefined, grad: "from-sky-500 to-blue-600", ring: "ring-sky-400/40" },
    { icon: MessageCircle, label: t("contactPage.whatsapp"), value: info.whatsapp || "—", href: info.whatsapp ? `https://wa.me/${info.whatsapp.replace(/\D/g,"")}` : undefined, grad: "from-emerald-500 to-green-600", ring: "ring-emerald-400/40" },
    { icon: Mail, label: t("contactPage.email"), value: info.email || "—", href: info.email ? `mailto:${info.email}` : undefined, grad: "from-rose-500 to-red-600", ring: "ring-rose-400/40" },
    { icon: MapPin, label: t("contactPage.office"), value: info.address || t("contactPage.officeFallback"), grad: "from-orange-500 to-amber-600", ring: "ring-orange-400/40" },
    { icon: Clock, label: t("contactPage.hours"), value: info.hours || "—", grad: "from-violet-500 to-purple-600", ring: "ring-violet-400/40" },
  ] : [];

  const socials = [
    { icon: Facebook, href: info?.facebook || "#", label: "Facebook", grad: "from-blue-500 to-blue-700" },
    { icon: MessageCircle, href: info?.whatsapp ? `https://wa.me/${info.whatsapp.replace(/\D/g,"")}` : "#", label: "WhatsApp", grad: "from-emerald-500 to-green-600" },
    { icon: Instagram, href: info?.instagram || "#", label: "Instagram", grad: "from-pink-500 via-fuchsia-500 to-orange-500" },
    { icon: Linkedin, href: info?.linkedin || "#", label: "LinkedIn", grad: "from-sky-600 to-blue-800" },
    { icon: Youtube, href: info?.youtube || "#", label: "YouTube", grad: "from-red-500 to-red-700" },
    { icon: Twitter, href: info?.twitter || "#", label: "X", grad: "from-slate-700 to-black" },
  ];

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-20">
        <div className="container-page">
          <h1 className="sr-only">Contact Online Tax VAT Bangladesh</h1>
          <SectionHeading
            eyebrow={t("contactPage.eyebrow")}
            title={<>{t("contactPage.titleA")}<span className="gradient-text">{t("contactPage.titleB")}</span></>}
            description={t("contactPage.desc")}
          />

          <div className="mt-12 grid lg:grid-cols-3 gap-6">
            <div className="relative space-y-4">
              {/* Watermark social icons */}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                <Facebook className="absolute -top-4 -left-3 h-24 w-24 text-blue-500/[0.06] dark:text-blue-400/[0.08] rotate-[-12deg]" />
                <Instagram className="absolute top-1/3 -right-4 h-28 w-28 text-pink-500/[0.06] dark:text-pink-400/[0.08] rotate-12" />
                <MessageCircle className="absolute bottom-10 -left-4 h-28 w-28 text-emerald-500/[0.06] dark:text-emerald-400/[0.08]" />
                <Linkedin className="absolute -bottom-4 right-6 h-24 w-24 text-sky-600/[0.06] dark:text-sky-400/[0.08] rotate-[8deg]" />
              </div>

              {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> :
              items.map((c, i) => (
                <a
                  key={i}
                  href={c.href}
                  target={c.href?.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden block rounded-2xl p-5 border border-border/60 bg-card/80 backdrop-blur-xl shadow-card hover:shadow-glow hover:-translate-y-0.5 hover:border-primary/40 transition-all"
                >
                  <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden rounded-2xl">
                    <span className="absolute top-0 -left-1/3 h-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 group-hover:translate-x-[450%] transition-all duration-[1100ms] ease-out" />
                  </span>
                  <div className="relative flex items-start gap-3">
                    <div className={`h-12 w-12 rounded-xl bg-gradient-to-br ${c.grad} text-white flex items-center justify-center shadow-lg ring-2 ${c.ring} shrink-0 group-hover:scale-110 transition-transform`}>
                      <c.icon className="h-5 w-5" strokeWidth={2.4} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{c.label}</p>
                      <p className="font-bold mt-0.5 break-all text-foreground">{c.value}</p>
                    </div>
                  </div>
                </a>
              ))}

              {/* Social row */}
              <div className="relative rounded-2xl border border-border/60 bg-card/80 backdrop-blur-xl p-4 shadow-card">
                <p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">Follow us</p>
                <div className="flex flex-wrap gap-2">
                  {socials.map((s, i) => (
                    <a
                      key={i}
                      href={s.href}
                      target="_blank" rel="noopener noreferrer"
                      aria-label={s.label}
                      className={`group/sb relative overflow-hidden h-10 w-10 rounded-xl bg-gradient-to-br ${s.grad} text-white flex items-center justify-center shadow-md hover:shadow-glow hover:-translate-y-0.5 hover:scale-110 transition-all`}
                    >
                      <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                        <span className="absolute top-0 -left-1/3 h-full w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-0 group-hover/sb:opacity-100 group-hover/sb:translate-x-[450%] transition-all duration-[900ms] ease-out" />
                      </span>
                      <s.icon className="h-5 w-5 relative" strokeWidth={2.4} />
                    </a>
                  ))}
                </div>
              </div>
            </div>


            <section aria-label="Before you contact us" className="lg:col-span-2 -mb-2 text-left">
              {i18n.language?.startsWith("bn") ? (
                <>
                  <h2 className="font-display font-bold text-lg md:text-xl mb-2">আমাদের সাপোর্ট টিমের সাথে যোগাযোগের আগে কিছু প্রয়োজনীয় তথ্য</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    আপনাকে সঠিক ফি (Quote) জানাতে এবং দ্রুত পরামর্শ প্রদানের সুবিধার্থে, ফর্মটি পাঠানোর আগে দয়া করে কয়েকটি মৌলিক তথ্য ও কাগজপত্র সাথে রাখুন।
                  </p>
                  <ul className="mt-3 space-y-2 text-sm text-muted-foreground leading-relaxed list-disc pl-5">
                    <li>
                      <span className="font-bold text-foreground">ব্যক্তিগত আয়কর সংক্রান্ত তথ্যের জন্য:</span> আপনার <strong>NID নাম্বার</strong>, <strong>ই-টিন (e-TIN) সার্টিফিকেট</strong> (অথবা এটির সাথে লিংক করা মোবাইল নাম্বার), সর্বশেষ <strong>স্যালারি সার্টিফিকেট</strong> (বেতন বিবরণী) বা লাভ-ক্ষতির হিসাব এবং চলতি আয় বছরের (১ জুলাই – ৩০ জুন) <strong>ব্যাংক স্টেটমেন্ট</strong> হাতের কাছে রাখুন।
                    </li>
                    <li>
                      <span className="font-bold text-foreground">ব্যবসা, ভ্যাট বা ট্রেড লাইসেন্স সংক্রান্ত তথ্যের জন্য:</span> আপনার <strong>BIN / ভ্যাট রেজিস্ট্রেশন নাম্বার</strong>, সর্বশেষ <strong>ট্রেড লাইসেন্স</strong>, কোম্পানির <strong>ইনকর্পোরেশন সার্টিফিকেট</strong> এবং প্রযোজ্য ক্ষেত্রে সর্বশেষ দাখিলকৃত <strong>মূসক ৯.১</strong> এর কপি সাথে রাখুন।
                    </li>
                  </ul>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                    আপনার মূল উদ্দেশ্যটি মাত্র এক লাইনে সংক্ষেপে শেয়ার করুন — যেমন: “২ বছরের ভিসা ট্যাক্স ফাইল জমা দিতে চাই”, “নতুন ই-কমার্স ব্যবসার জন্য বিন (BIN) নিবন্ধন করতে চাই” অথবা “ভুলভাবে অ্যাসেসমেন্ট হওয়া রিটার্ন সংশোধন করতে চাই”। এটি আমাদের উপদেষ্টাকে প্রথম জবাবেই আপনার জন্য সঠিক ফি, প্রয়োজনীয় ডকুমেন্টের তালিকা এবং কতদিনের মধ্যে কাজটি সম্পন্ন হবে তা নিখুঁতভাবে প্রস্তুত করতে সাহায্য করবে।
                  </p>
                </>
              ) : (
                <>
                  <h2 className="font-display font-bold text-lg md:text-xl mb-2">Before You Contact Our Support Team</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    To give you an accurate quote and a faster consultation, please keep a few basics ready before you send
                    the form. For personal tax queries, have your <strong>NID number</strong>, <strong>e-TIN certificate</strong>
                    (or the mobile number linked to it), most recent <strong>salary certificate</strong> or profit &amp; loss
                    statement, and bank statements for the current income year (1 July – 30 June) at hand. For business,
                    VAT or trade license queries, please note your <strong>BIN / VAT registration number</strong>, latest
                    <strong> Trade License</strong>, company incorporation certificate and, if applicable, the last filed
                    Mushak 9.1. Sharing a one-line description of what you want to achieve — e.g. “file a 2-year visa tax
                    file”, “register BIN for a new e-commerce business”, or “correct a wrongly assessed return” — lets our
                    advisor prepare exact fees, document checklist and turnaround time on the first reply.
                  </p>
                </>
              )}
            </section>


            <form

              ref={formRef}
              onSubmit={(e) => {
                e.preventDefault();
                const fd = new FormData(e.currentTarget);
                const msg = `Hi, my name is ${fd.get("name")}.\n\n${fd.get("message")}`;
                const wa = info?.whatsapp?.replace(/\D/g, "");
                if (wa) window.open(`https://wa.me/${wa}?text=${encodeURIComponent(msg)}`, "_blank");
                formRef.current?.reset();
                setSubmitted(true);
                setTimeout(() => setSubmitted(false), 8000);
              }}
              className="lg:col-span-2 glass rounded-2xl p-6 shadow-card space-y-4"
              style={{ minHeight: 620, contain: "layout style" }}

            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{t("contactPage.fullName")}</Label>
                  <Input id="name" name="name" required maxLength={100} className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="phone">{t("contactPage.phone")}</Label>
                  <Input id="phone" name="phone" required maxLength={20} className="mt-1.5" placeholder="01XXXXXXXXX" />
                </div>
              </div>
              <div>
                <Label htmlFor="email">{t("contactPage.emailLabel")}</Label>
                <Input id="email" name="email" type="email" maxLength={120} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="subject">{t("contactPage.subject")}</Label>
                <Input id="subject" name="subject" required maxLength={150} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="message">{t("contactPage.message")}</Label>
                <Textarea id="message" name="message" required maxLength={1000} rows={5} className="mt-1.5" />
              </div>
              <Button type="submit" className="w-full sm:w-auto gradient-primary text-primary-foreground shadow-glow">
                <Send className="h-4 w-4 mr-1.5" /> {t("contactPage.send")}
              </Button>

              <Suspense fallback={null}>
                <SubmittedToast show={submitted} />
              </Suspense>

            </form>
          </div>

          <div className="mt-16 max-w-3xl mx-auto text-center text-sm leading-relaxed text-muted-foreground space-y-3">
            <p>
              Our advisors respond Sunday to Thursday from 10:00 AM to 7:00 PM (Bangladesh Standard Time), with
              limited Saturday support for active filings and urgent NBR deadlines. WhatsApp and hotline messages
              received outside office hours are picked up first thing the next working morning. Email and contact
              form submissions are answered within one business day, and we keep written records of every
              conversation so your file stays consistent across consultants and renewals.
            </p>
            <p>
              A typical consultation starts with a short discovery call where we understand your business, current
              tax position and goal — VAT registration (BIN), VAT return (Mushak 9.1), e-TIN, income tax return,
              trade license, company registration or visa tax file. We then share a transparent BDT quote, the
              exact document checklist, the expected NBR turnaround and the digital channels we will use to deliver
              your acknowledgement, certificate or challan. There is no obligation to proceed after the call.
            </p>
            <p>
              For urgent embassy submissions, audit notices, refund disputes or last-minute return filings, please
              mention &ldquo;urgent&rdquo; in your subject line so the case is escalated to a senior consultant. Our Dhaka
              office welcomes scheduled in-person visits, and we also serve clients in Chattogram, Sylhet, Khulna,
              Rajshahi and Bangladeshi taxpayers living abroad through fully digital onboarding.
            </p>
          </div>

          {(() => { const c = SEO_CONTENT["contact"]; return <SeoFaqBlock title={c.title} intro={c.intro} bullets={c.bullets} faqs={c.faqs} />; })()}
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["contact"]} />
        </div>
      </section>
      <HiddenSeoContext topic="contact" />
    </SiteShell>
  );
}
